import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import api from '../../../services/api';
import { displayTitle, readNokeError } from './format';
import { RestockV2GalleryImage, RestockV2ThumbTarget } from './types';

interface GalleryDialogProps {
  target: RestockV2ThumbTarget | null;
  onClose: () => void;
}

// One dialog for all three screens: tapping any row's photo lands here.
// With a variant_id we fetch the whole product gallery; without one the row's single image is all there is.
const GalleryDialog: React.FC<GalleryDialogProps> = ({ target, onClose }) => {
  const [images, setImages] = useState<RestockV2GalleryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setImages([]);
    setError('');
    const variantId = target?.variant_id;
    if (!target || variantId === null || variantId === undefined) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    api.get<{ images?: RestockV2GalleryImage[] }>(`/api/noke/inventory/${variantId}/images`)
      .then((response) => { if (alive) setImages(response.data?.images || []); })
      .catch((err) => { if (alive) setError(readNokeError(err, '載入商品圖片失敗，請稍後再試。')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [target]);

  const title = target ? (displayTitle(target.title) || target.sku) : '';
  const sorted = images.slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).filter((image) => !!image.src);
  // Fall back to the row's own thumbnail so a variant with no stored gallery still shows the garment large.
  const fallback = !loading && !error && sorted.length === 0 && target?.image_url
    ? [{ src: target.image_url, position: 1 }] : [];
  const shown = sorted.length ? sorted : fallback;

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="restock-v2-gallery-title">
      <DialogTitle id="restock-v2-gallery-title" sx={{ pb: 1 }}>
        {title}
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{target?.sku}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box role="status" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!!error && !loading && <Alert severity="error">{error}</Alert>}
        {!loading && !error && shown.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>沒有商品圖片。</Typography>
        )}
        {!loading && shown.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            {shown.map((image, index) => (
              <Box
                key={`${image.src}:${index}`}
                sx={{
                  position: 'relative', pt: '125%', borderRadius: 1, overflow: 'hidden',
                  bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider',
                }}
              >
                <Box
                  component="img" src={image.src} alt={`${title} ${index + 1}`} loading="lazy"
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>關閉</Button></DialogActions>
    </Dialog>
  );
};

export default GalleryDialog;
